
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { AlertCircle, Database, Key, Save, Server, ShieldCheck } from 'lucide-react';
import { fetchSystemSettings, updateSystemSettings } from '@/services/systemSettingsService';
import { useQuery } from '@tanstack/react-query';
import { Form, FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

// Define schema for system settings form
const systemSettingsSchema = z.object({
  siteName: z.string().min(1, "Site name is required"),
  siteDescription: z.string().optional(),
  allowRegistration: z.boolean().default(true),
  requireEmailVerification: z.boolean().default(true),
  maxUploadSize: z.number().min(1).max(100),
  adminNotificationEmail: z.string().email().optional().or(z.literal('')),
  defaultUserRole: z.enum(['student', 'mentor', 'admin']).default('student'),
});

type SystemSettingsFormValues = z.infer<typeof systemSettingsSchema>;

const SystemSettings = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('general');
  
  // Mock system settings - in a real application, these would be fetched from the database
  const mockSettings = {
    general: {
      siteName: 'Prompted Forge Bootcamp',
      siteDescription: 'AI agent training platform for developers',
      allowRegistration: true,
      requireEmailVerification: true,
    },
    security: {
      maxUploadSize: 10, // MB
      adminNotificationEmail: 'admin@example.com',
      defaultUserRole: 'student' as const,
    }
  };
  
  const form = useForm<SystemSettingsFormValues>({
    resolver: zodResolver(systemSettingsSchema),
    defaultValues: {
      siteName: mockSettings.general.siteName,
      siteDescription: mockSettings.general.siteDescription,
      allowRegistration: mockSettings.general.allowRegistration,
      requireEmailVerification: mockSettings.general.requireEmailVerification,
      maxUploadSize: mockSettings.security.maxUploadSize,
      adminNotificationEmail: mockSettings.security.adminNotificationEmail,
      defaultUserRole: mockSettings.security.defaultUserRole,
    }
  });
  
  useEffect(() => {
    // Redirect if not an admin
    if (!isAdmin()) {
      navigate('/dashboard');
    }
  }, [isAdmin, navigate]);
  
  // Handle form submission
  const onSubmit = (data: SystemSettingsFormValues) => {
    console.log('Submitted settings:', data);
    toast.success('Settings updated successfully');
  };
  
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold section-header">System Settings</h1>
        <p className="text-muted-foreground section-subheader">Configure platform settings and preferences</p>
      </div>
      
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Warning</AlertTitle>
        <AlertDescription>
          Changes to these settings will affect all users on the platform. Proceed with caution.
        </AlertDescription>
      </Alert>
      
      <Tabs defaultValue="general" value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
          <TabsTrigger value="modules">Modules</TabsTrigger>
        </TabsList>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <TabsContent value="general" className="mt-4 space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="card-title">Platform Settings</CardTitle>
                  <CardDescription>
                    Configure your bootcamp platform details
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="siteName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Site Name</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormDescription>
                          This will be displayed in the browser tab and throughout the platform
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="siteDescription"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Site Description</FormLabel>
                        <FormControl>
                          <Textarea {...field} />
                        </FormControl>
                        <FormDescription>
                          Brief description of your bootcamp platform
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <Separator />
                  
                  <FormField
                    control={form.control}
                    name="allowRegistration"
                    render={({ field }) => (
                      <FormItem className="flex justify-between items-center gap-2">
                        <div>
                          <FormLabel>Allow Public Registration</FormLabel>
                          <FormDescription>
                            Allow users to register without an invitation
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch 
                            checked={field.value} 
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="requireEmailVerification"
                    render={({ field }) => (
                      <FormItem className="flex justify-between items-center gap-2">
                        <div>
                          <FormLabel>Require Email Verification</FormLabel>
                          <FormDescription>
                            Users must verify their email before accessing the platform
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch 
                            checked={field.value} 
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="security" className="mt-4 space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="card-title">Security Settings</CardTitle>
                  <CardDescription>
                    Configure security settings for your platform
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="maxUploadSize"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Max Upload Size (MB)</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            min={1} 
                            max={100} 
                            {...field} 
                            onChange={e => field.onChange(Number(e.target.value))}
                          />
                        </FormControl>
                        <FormDescription>
                          Maximum file size for user uploads
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="adminNotificationEmail"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Admin Notification Email</FormLabel>
                        <FormControl>
                          <Input type="email" {...field} />
                        </FormControl>
                        <FormDescription>
                          Email address for system notifications
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="defaultUserRole"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Default User Role</FormLabel>
                        <FormControl>
                          <select 
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2"
                            {...field}
                          >
                            <option value="student">Student</option>
                            <option value="mentor">Mentor</option>
                            <option value="admin">Admin</option>
                          </select>
                        </FormControl>
                        <FormDescription>
                          Default role assigned to new users
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="modules" className="mt-4 space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="card-title">Module Settings</CardTitle>
                  <CardDescription>
                    Configure learning module settings
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center gap-2">
                    <div>
                      <Label>Enable Module Progression</Label>
                      <p className="text-sm text-muted-foreground">
                        Students must complete prerequisites before accessing advanced modules
                      </p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                  
                  <div className="flex justify-between items-center gap-2">
                    <div>
                      <Label>Enable Module Reflections</Label>
                      <p className="text-sm text-muted-foreground">
                        Students must complete reflections to mark modules as complete
                      </p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                  
                  <div className="flex justify-between items-center gap-2">
                    <div>
                      <Label>Show Completion Metrics</Label>
                      <p className="text-sm text-muted-foreground">
                        Display completion metrics on the dashboard
                      </p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                  
                  <div className="flex justify-between items-center gap-2">
                    <div>
                      <Label>Enable Student Projects</Label>
                      <p className="text-sm text-muted-foreground">
                        Allow students to create and submit projects
                      </p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <div className="mt-6 flex justify-end">
              <Button type="submit" className="flex items-center gap-2">
                <Save className="h-4 w-4" /> Save Settings
              </Button>
            </div>
          </form>
        </Form>
      </Tabs>
    </div>
  );
};

export default SystemSettings;
